import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_LOST_FOUND_ITEMS } from '../graphql/lost-found-queries';
import LostFoundItem from '../components/LostFoundItem';
import Spinner from '../components/Spinner';
import { LOST_FOUND_CATEGORY } from '../components/LostFoundForm';

const ITEMS_PER_PAGE = 10;

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All categories' },
  { value: LOST_FOUND_CATEGORY.LOST_ITEM_FOUND, label: 'Lost Item Found' },
  { value: LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM, label: 'Reported Lost Item' },
];

export default function LostAndFoundArchive() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const loader = useRef(null);

  const { loading, data, error, fetchMore } = useQuery(GET_LOST_FOUND_ITEMS, {
    variables: {
      page: 1,
      limit: ITEMS_PER_PAGE,
      search: search || undefined,
      category: categoryFilter || undefined,
      isArchived: true,
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (data?.lostFoundItems) {
      setItems(data.lostFoundItems.items);
      setHasMore(data.lostFoundItems.hasMore);
      setPage(1);
    }
  }, [data]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 1.0,
    };

    // Observer for infinite scrolling
    const observer = new IntersectionObserver(handleObserver, options);
    if (loader.current) {
      observer.observe(loader.current);
    }

    return () => {
      if (loader.current) {
        observer.unobserve(loader.current);
      }
    };
  }, []);

  const handleObserver = (entities) => {
    const target = entities[0];
    if (target.isIntersecting && hasMore && !loading) {
      loadMoreItems();
    }
  };

  const loadMoreItems = () => {
    const nextPage = page + 1;
    fetchMore({
      variables: {
        page: nextPage,
        limit: ITEMS_PER_PAGE,
        search: search || undefined,
        category: categoryFilter || undefined,
        isArchived: true,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          lostFoundItems: {
            ...fetchMoreResult.lostFoundItems,
            items: [
              ...prev.lostFoundItems.items,
              ...fetchMoreResult.lostFoundItems.items,
            ],
          },
        };
      },
    }).then(() => {
      setPage(nextPage);
      setHasMore(data.lostFoundItems.hasMore);
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // The query will be re-run with the new search term
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Lost and Found Archive</h1>
        <div className="flex space-x-3">
          <Link
            to="/dashboard/lost-found"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
          >
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Back to Active Items
          </Link>
        </div>
      </div>

      {/* Search and filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          {/* Search group: input + button together */}
          <div className="flex flex-1 min-w-0 gap-2">
            <div className="relative flex-1 min-w-0">
              <input
                type="text"
                className="block w-full h-10 rounded-lg border border-gray-300 bg-gray-50 py-2 pl-10 pr-4 text-gray-900 placeholder-gray-500 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Search by property or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search archived items"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center h-10 px-4 rounded-lg border border-transparent bg-blue-600 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex-shrink-0"
            >
              Search
            </button>
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <label htmlFor="archive-category-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Category
            </label>
            <select
              id="archive-category-filter"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="h-10 min-w-[180px] rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 pr-8 text-sm text-gray-900 transition-colors focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              aria-label="Filter by category"
            >
              {CATEGORY_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>

      {/* Lost and Found Items List */}
      <div className="space-y-4">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-sm font-medium text-red-800">Unable to load archived lost and found items</h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
          </div>
        ) : loading && page === 1 ? (
          <div className="flex justify-center p-12">
            <Spinner />
          </div>
        ) : items.length > 0 ? (
          <>
            {items.map((item) => (
              <LostFoundItem 
                key={item.id} 
                item={item} 
              />
            ))}
            {loading && (
              <div className="flex justify-center p-4">
                <Spinner />
              </div>
            )}
            <div ref={loader} className="h-10" />
          </>
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No archived items</h3>
            <p className="mt-1 text-sm text-gray-500">The archive is empty. Items marked as 'Returned' or 'Discarded' will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
} 
